import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { OrgPlanningScenarioReview } from '../models/OrgPlanningScenarioReview';
import { canReadOrgPlanningReview, orgPlanningReviewKey } from './orgPlanningReviewKey';
import { useOrgPlanningReviewScope } from './useOrgPlanningReviewScope';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import {
  enqueueSuccessionTransition,
} from './orgPlanningCommandSequencer';
import { useAtlasAccessSummary } from './useAtlasAccessSummary';
import { mapAtlasActionState } from '../services/atlasActionStatePresentation';
import type { AtlasActionState } from '../models/AtlasAccessSummary';
import type { OrganizationChangeApplyResult } from '../models/OrganizationChangeApplyResult';

/**
 * A draft's apply permission is resolved independently of its ability to be
 * staged. `null` is reserved for a genuinely unavailable access result;
 * every other non-enabled result is a settled denial and must not look like a
 * request that is still loading.
 */
export function resolveServerDraftApplyAccess(state: AtlasActionState): {
  readonly mayApply: boolean | null;
  readonly message: string | null;
} {
  if (state.state === 'enabled') return { mayApply: true, message: null };
  if (state.state === 'unavailable') {
    return { mayApply: null, message: mapAtlasActionState(state).message };
  }
  if (state.state === 'pending' && state.reason === 'approval') {
    return { mayApply: false, message: null };
  }
  if (state.state === 'blocked' && state.reason === 'role') {
    return {
      mayApply: false,
      message: 'Applying a scenario to the live org chart requires Moderator or Owner folder access. You can still stage succession changes.',
    };
  }
  return { mayApply: false, message: mapAtlasActionState(state).message };
}

export function resolveServerDraftHead(
  selected: { readonly headChangeSetId: string } | undefined,
  projectionHeadChangeSetId?: string | null,
): string | null {
  return selected?.headChangeSetId ?? projectionHeadChangeSetId ?? null;
}

export function serverDraftTransitionPath(input: {
  readonly folderId: string;
  readonly diagramId: string;
  readonly changeSetId: string;
  readonly action: 'apply' | 'discard';
}): string {
  const base = `/api/folders/${input.folderId}/diagrams/${input.diagramId}/atlas`;
  return input.action === 'apply'
    ? `${base}/organization-changes/${input.changeSetId}/apply`
    : `${base}/organization-plans/${input.changeSetId}/discard`;
}

/**
 * Places a lifecycle transition behind every immutable-head amendment already
 * queued for this diagram, then resolves the head again at execution time.
 * A render-time head is only a fallback: it must never close an older draft
 * while a newer accepted head is still advancing through the same lane.
 */
export function enqueueServerDraftTransition<T>(input: {
  context: string;
  projectionHeadChangeSetId?: string | null;
  getSelected: () => { readonly headChangeSetId: string } | undefined;
  action: 'apply' | 'discard';
  run: (headChangeSetId: string, signal: AbortSignal) => Promise<T>;
  queryClient?: QueryClient;
}): Promise<T> {
  return enqueueSuccessionTransition(input.context, async (signal) => {
    const latestHeadChangeSetId = resolveServerDraftHead(
      input.getSelected(),
      input.projectionHeadChangeSetId,
    );
    if (!latestHeadChangeSetId) throw new Error('No draft to act on.');
    return input.run(latestHeadChangeSetId, signal);
  }, input.queryClient, {
    lifecycleKey: input.action,
    allowFailedPrerequisites: input.action === 'discard',
  });
}

/**
 * Apply/discard for the chart's open server draft, usable from status
 * surfaces (the assistant panel's draft banner).
 *
 * Until this existed the only Apply for a server-staged draft lived in the
 * property panel's succession section — reachable only with a person selected
 * and the succession lens active. From the Default view a staged draft showed
 * its chip and offered nothing: staged, visible, unactionable.
 *
 * `mayApply` comes from the principal-scoped access summary, primed by the
 * accepted save response, so the banner can enable Apply without waiting for
 * the full review payload while the server remains authoritative.
 */
export function useServerDraftActions(input: {
  folderId?: string | null;
  diagramId?: string | null;
  /** Projection-derived fallback for a reloaded plan with no client selection. */
  headChangeSetId?: string | null;
  enabled?: boolean;
}) {
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const reviewScope = useOrgPlanningReviewScope(folderId, diagramId);
  const authenticatedFetch = useAuthenticatedFetch();
  const selected = useOrgPlanningScenarioStore((state) =>
    diagramId ? state.selectedByDiagram[diagramId] : undefined,
  );
  const headChangeSetId = resolveServerDraftHead(selected, input.headChangeSetId);
  const access = useAtlasAccessSummary({
    folderId,
    diagramId,
    context: headChangeSetId ? { kind: 'scenario', changeSetId: headChangeSetId } : undefined,
    enabled: Boolean((input.enabled ?? true) && headChangeSetId),
  });

  const review = useQuery({
    queryKey: orgPlanningReviewKey(reviewScope, headChangeSetId),
    enabled: Boolean((input.enabled ?? true) && canReadOrgPlanningReview(reviewScope) && headChangeSetId),
    staleTime: 5_000,
    queryFn: async ({ signal }): Promise<OrgPlanningScenarioReview> => {
      const response = await authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${headChangeSetId}/review`,
        { signal },
      );
      if (!response.ok) throw new Error(`Failed to load draft review (${response.status})`);
      return response.json() as Promise<OrgPlanningScenarioReview>;
    },
  });


  const applyAccess = resolveServerDraftApplyAccess(access.actionState('applyScenario'));

  return {
    headChangeSetId,
    /** null only while the principal-scoped access summary is unavailable. */
    mayApply: applyAccess.mayApply,
    /** A settled reason why this actor cannot apply the draft, if applicable. */
    applyAccessMessage: applyAccess.message,
    /** The server's full account of the draft, for detail surfaces. */
    review: review.data ?? null,
    isOperational: Boolean(review.data?.operational),
    execution: review.data?.operational?.execution ?? null,
  };
}

export function shouldLoadOperationalStatus(review: OrgPlanningScenarioReview | undefined): boolean {
  return Boolean(review?.operational && [
    'writeback_pending',
    'written_back',
    'partially_applied',
    'failed',
    'reconciled',
  ].includes(review.lifecycle));
}

export function assertServerDraftSettlement(
  action: 'apply' | 'discard',
  expectedHeadChangeSetId: string,
  body: Partial<OrganizationChangeApplyResult>,
): asserts body is Partial<OrganizationChangeApplyResult> & { graphVersion: number } {
  if (action === 'apply' && body.changeSetId !== expectedHeadChangeSetId) {
    throw new Error('The server did not confirm the exact draft head. Refresh before retrying.');
  }
  if (!Number.isFinite(body.graphVersion)) {
    throw new Error('The server accepted the request without a verifiable organization version. Refresh before retrying.');
  }
}
